/**
 * Locale Resolution - Single Source of Truth
 * 
 * Following Airbnb/LinkedIn international platform standards:
 * - IP geolocation for FORMATTING only (currency, timezone)
 * - Browser language for LANGUAGE selection
 * - User explicit choice always wins
 * 
 * CRITICAL: This module provides bootstrap-only resolution.
 * For runtime resolution, use resolveCanonicalLocale() with DB-loaded supportedLngs.
 */

// Detection sources in priority order (following industry standard)
export const DETECTION_SOURCES = [
  'ACCOUNT',     // 1. User's saved account preference
  'STORED',      // 2. localStorage from previous visit
  'BROWSER',     // 3. navigator.languages array
  'IP_FORMAT',   // 4. IP for formatting ONLY (not language)
  'DEFAULT',     // 5. Final fallback
] as const;

export type DetectionSource = typeof DETECTION_SOURCES[number];

/**
 * Browsers can expose POSIX-style locale suffixes such as `en-US@posix`.
 * They are useful to the operating system but invalid BCP 47 tags for Intl.
 */
export function normalizeBrowserLocale(locale?: string): string {
  const candidate = locale?.trim().replace(/[@.].*$/, '').replace(/_/g, '-');
  if (!candidate) return 'en-US';
  try {
    return Intl.getCanonicalLocales(candidate)[0] ?? 'en-US';
  } catch {
    const base = candidate.split('-')[0]?.toLowerCase();
    return DEFAULT_REGIONAL[base] ?? 'en-US';
  }
}

// locale-data: bootstrap-regional-defaults - Static fallback for app initialization
// At runtime, use DB supportedLngs for more accurate resolution
const DEFAULT_REGIONAL: Record<string, string> = {
  'ar': 'ar-AE', 'cs': 'cs-CZ', 'da': 'da-DK', 'de': 'de-DE',
  'el': 'el-GR', 'en': 'en-US', 'es': 'es-ES', 'fa': 'fa-IR',
  'fi': 'fi-FI', 'fr': 'fr-FR', 'he': 'he-IL', 'hi': 'hi-IN',
  'hu': 'hu-HU', 'id': 'id-ID', 'it': 'it-IT', 'ja': 'ja-JP',
  'ko': 'ko-KR', 'ms': 'ms-MY', 'nb': 'nb-NO', 'nl': 'nl-NL',
  'pl': 'pl-PL', 'pt': 'pt-BR', 'ro': 'ro-RO', 'ru': 'ru-RU',
  'sv': 'sv-SE', 'th': 'th-TH', 'tr': 'tr-TR', 'uk': 'uk-UA',
  'vi': 'vi-VN', 'zh': 'zh-CN', 'zh-TW': 'zh-TW',
};

// Supported base languages
const SUPPORTED_LANGUAGES = Object.keys(DEFAULT_REGIONAL);

export interface LocaleResolution {
  language: string;           // Base language: "es"
  i18nLocale: string;         // For translation files: "es-AR"
  formattingLocale: string;   // For Intl APIs: "es-AR"
  source: DetectionSource;
  timezone?: string;
  currency?: string;
}

/**
 * Match browser languages against supported languages
 * Uses navigator.languages array (ordered by preference)
 * 
 * @returns Best matching language info or null
 */
export function matchBrowserLanguages(): { 
  language: string; 
  browserLocale: string; 
  i18nLocale: string; 
} | null {
  const browserLanguages = typeof navigator !== 'undefined' 
    ? (navigator.languages || [navigator.language]) 
    : [];
  
  for (const browserLang of browserLanguages) {
    if (!browserLang) continue;
    const normalizedBrowserLocale = normalizeBrowserLocale(browserLang);
    const base = normalizedBrowserLocale.split('-')[0];
    
    // Check if we support this language
    if (SUPPORTED_LANGUAGES.includes(base) || SUPPORTED_LANGUAGES.includes(normalizedBrowserLocale)) {
      const i18nLocale = resolveI18nLocale(base, normalizedBrowserLocale);
      return {
        language: base,
        browserLocale: normalizedBrowserLocale,
        i18nLocale,
      };
    }
  }
  
  return null;
}

/**
 * Resolve i18n locale from base language + browser context (BOOTSTRAP ONLY)
 * 
 * Rule: Use browser's regional variant if it matches the selected language
 * and we have translation files for it.
 * 
 * @param selectedLanguage - Base language code (e.g., "es")
 * @param browserLocale - Browser's navigator.language (e.g., "es-AR", "en-US")
 * @returns Best matching i18n locale (e.g., "es-AR" or "es-ES")
 */
export function resolveI18nLocale(
  selectedLanguage: string, 
  browserLocale?: string
): string {
  const normalizedBrowserLocale = browserLocale
    ? normalizeBrowserLocale(browserLocale)
    : undefined;
  const browserBase = normalizedBrowserLocale?.split('-')[0];
  
  // If browser matches selected language, prefer browser's regional variant
  if (normalizedBrowserLocale && browserBase === selectedLanguage) {
    // At bootstrap, assume browser locale is valid (DB will normalize later)
    return normalizedBrowserLocale;
  }
  
  // Fallback to default regional variant for this language
  return DEFAULT_REGIONAL[selectedLanguage] || 'en-US';
}

/**
 * RUNTIME: Resolve canonical locale from input + DB-supported locales
 * This is the primary resolver that should be used after DB configs load
 * 
 * @param input - User/browser requested locale (e.g., "de", "de-AT", "de-DE")
 * @param supportedLngs - Array of DB-enabled locales from useLocaleConfigs
 * @param browserLocale - Optional browser locale for regional preference
 * @returns Canonical locale that exists in supportedLngs
 */
/**
 * RUNTIME: Resolve canonical locale from input + DB-supported locales
 * This is the primary resolver that should be used after DB configs load
 * 
 * CRITICAL: This function ensures we always return a locale that exists in supportedLngs.
 * When user selects "de" (base language), this will find "de-DE" if that's what DB supports.
 * 
 * @param input - User/browser requested locale (e.g., "de", "de-AT", "de-DE")
 * @param supportedLngs - Array of DB-enabled locales from useLocaleConfigs
 * @param browserLocale - Optional browser locale for regional preference
 * @returns Canonical locale that exists in supportedLngs
 */
export function resolveCanonicalLocale(
  input: string,
  supportedLngs: string[],
  browserLocale?: string
): string {
  if (!input || supportedLngs.length === 0) return 'en-US';
  
  // 1. Exact match - input is already a supported locale
  if (supportedLngs.includes(input)) {
    return input;
  }
  
  const baseLanguage = input.split('-')[0];
  const browserBase = browserLocale?.split('-')[0];
  
  // 2. Browser regional match - if browser matches input language and is supported
  if (browserLocale && browserBase === baseLanguage && supportedLngs.includes(browserLocale)) {
    return browserLocale;
  }
  
  // 3. Find regional variant with same base language (e.g., "de" -> "de-DE")
  // Prefer the default regional from our hardcoded map if it exists in supportedLngs
  const defaultRegional = DEFAULT_REGIONAL[baseLanguage];
  if (defaultRegional && supportedLngs.includes(defaultRegional)) {
    return defaultRegional;
  }
  
  // 4. Find ANY supported locale with same base language
  const matchedLocale = supportedLngs.find(l => l.startsWith(baseLanguage + '-'));
  if (matchedLocale) {
    return matchedLocale;
  }
  
  // 5. Check if base language itself is supported (rare, but possible)
  if (supportedLngs.includes(baseLanguage)) {
    return baseLanguage;
  }
  
  // 6. Ultimate fallback
  return supportedLngs.includes('en-US') ? 'en-US' : (supportedLngs[0] || 'en-US');
}

/**
 * Get the default regional locale for a base language
 */
export function getDefaultRegional(baseLanguage: string): string {
  return DEFAULT_REGIONAL[baseLanguage] || 'en-US';
}

// Default fallback configuration
export const DEFAULT_LOCALE_RESOLUTION: LocaleResolution = {
  language: 'en',
  i18nLocale: 'en-US',
  formattingLocale: 'en-US',
  source: 'DEFAULT',
  timezone: 'UTC',
  currency: 'USD',
};
